import { useEffect, useState } from 'react'
import { api, fmt } from '../lib/api.js'
import { Chip, Email, Empty, ErrorNote, Money, PriorityPicker } from '../components/ui.jsx'
import ContactDialog from '../components/ContactDialog.jsx'

const TOUCH_TYPES = ['email', 'call', 'linkedin', 'meeting', 'note']

// Split prose into bullets without breaking on decimals: "30.8% of sales." is
// one sentence, not two.
function sentences(text) {
  if (!text) return []
  return String(text)
    .split(/(?<=[a-zA-Z%)\]"'])\.\s+(?=[A-Z"'(])/)
    .map((s) => s.trim().replace(/\.$/, ''))
    .filter(Boolean)
}

// The figures are the point of these paragraphs, so make them findable.
const FIGURE = /((?:CHF|EUR|USD|GBP|NOK|SEK|DKK)\s?[\d,.]+(?:bn|m|k)?|[$£€]\s?[\d,.]+(?:bn|m|k)?|\b\d+(?:\.\d+)?\s?%|\b\d+(?:\.\d+)?x\b|\b\d{2,3}(?:\.\d+)?\s?days\b)/g

function Highlighted({ text }) {
  const parts = String(text).split(FIGURE)
  return (
    <>
      {parts.map((part, i) => (FIGURE.test(part) && i % 2 === 1 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>))}
    </>
  )
}

function Bullets({ text, note }) {
  const items = sentences(text)
  if (!items.length) return null
  return (
    <ul className="bullets">
      {items.map((s, i) => (
        <li key={i}>
          <Highlighted text={s} />
          {i === items.length - 1 && note ? (
            <span className="fn" title={note}>[src]</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

// A one-line read of both fit models for the tab badge.
function fitSummary(data) {
  const bits = []
  if (data.wc_fit?.fit) bits.push(`WC ${data.wc_fit.fit}`)
  if (data.proc_fit?.fit) bits.push(`Proc ${data.proc_fit.fit}`)
  return bits.join(' · ') || null
}

const BANDS = ['Strong', 'Possible', 'Weak', 'Unrated']

// A person can disagree with a model. The model's own answer stays visible
// beside the override so the two are never confused, and reverting is one click.
function OverrideControl({ slug, model, fit, onChanged }) {
  const [busy, setBusy] = useState(false)
  const set = async (band) => {
    setBusy(true)
    try {
      await api.setFitOverride(slug, { model, band: band === 'Unrated' && !fit.model_fit ? null : band })
      await onChanged()
    } finally { setBusy(false) }
  }
  return (
    <span className="row" style={{ gap: 6, marginLeft: 'auto' }}>
      {fit.overridden ? (
        <span className="card-sub" title={fit.override?.note || ''}>
          model said <strong>{fit.model_fit || 'Unrated'}</strong>
          {fit.override?.by ? ` · ${fit.override.by.split('@')[0]}` : ''}
        </span>
      ) : null}
      <select
        className="input"
        style={{ width: 118, padding: '2px 4px' }}
        value={fit.fit || 'Unrated'}
        disabled={busy}
        onClick={(ev) => ev.stopPropagation()}
        onChange={(ev) => set(ev.target.value)}
        title="Override the model"
      >
        {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
        {fit.model_fit === 'Below gate' || fit.fit === 'Below gate' ? <option value="Below gate">Below gate</option> : null}
      </select>
      {fit.overridden ? (
        <button className="btn link" disabled={busy}
          onClick={async () => { setBusy(true); try { await api.setFitOverride(slug, { model, band: null }); await onChanged() } finally { setBusy(false) } }}>
          revert
        </button>
      ) : null}
    </span>
  )
}

// The structural fit models, side by side. These ask a different question from
// the focus areas: not "has the company said this out loud" but "does the shape
// of this business make the problem likely".
//
// `criteria` is an array of {key,label,status,met,value,detail,bullets,source},
// and `pitch` is an object, not a string. Rendering the pitch object directly
// is what crashed this panel the first time.
function FitModel({ title, fit, slug, model, onChanged }) {
  if (!fit) return null
  const band = fit.fit
  const kind = band === 'Strong' ? 'named' : band === 'Possible' ? 'mentioned' : band ? 'not_now' : 'unresearched'
  const criteria = Array.isArray(fit.criteria) ? fit.criteria : []
  const p = fit.pitch || null

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
        <Chip kind={kind}>{band ? band.toUpperCase() : 'UNRATED'}</Chip>
        {fit.provisional ? <span className="card-sub">provisional</span> : null}
        <span className="card-sub">
          {fit.met} of {criteria.length || 4} met
          {fit.unresearched ? ` · ${fit.unresearched} not researched` : ''}
        </span>
        <OverrideControl slug={slug} model={model} fit={fit} onChanged={onChanged} />
      </div>
      <div className="card-body stack" style={{ gap: 9 }}>
        {fit.below_gate ? (
          <div style={{ background: 'var(--neg-soft)', border: '0.5px solid #F0D2D2', padding: '8px 11px', fontSize: 'var(--fs-pill)' }}>
            Below the spend gate{fit.gate?.detail ? ` (${fit.gate.detail})` : ''}. Not a weak prospect, simply not a prospect for this offer.
          </div>
        ) : null}

        {p ? (
          <div style={{ borderLeft: '2px solid var(--accent)', paddingLeft: 12 }}>
            {p.angle ? <div style={{ fontWeight: 600, marginBottom: 3 }}>{p.angle}</div> : null}
            {p.hook ? <div style={{ fontSize: 'var(--fs-pill)' }}>{p.hook}</div> : null}
            {Array.isArray(p.points) && p.points.length ? (
              <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 'var(--fs-pill)', color: 'var(--muted)' }}>
                {p.points.map((x, i) => <li key={i}>{typeof x === 'string' ? x : x?.text || ''}</li>)}
              </ul>
            ) : null}
            {p.ask ? (
              <div style={{ fontSize: 'var(--fs-pill)', marginTop: 5 }}>
                <span className="lbl">Ask</span> {p.ask}
              </div>
            ) : null}
          </div>
        ) : null}

        {criteria.map((c) => {
          const met = c.met === true
          const unknown = c.met == null
          return (
            <div key={c.key} style={{ borderTop: '0.5px solid var(--line-soft)', paddingTop: 7 }}>
              <div className="row" style={{ gap: 7, alignItems: 'baseline' }}>
                <Chip kind={met ? 'named' : unknown ? 'unresearched' : 'absent'}>
                  {met ? 'MET' : unknown ? 'NOT RESEARCHED' : 'NOT MET'}
                </Chip>
                <span style={{ fontWeight: 500 }}>{c.label || c.key}</span>
                {c.value ? (
                  <span className="mono" style={{ marginLeft: 'auto', fontSize: 'var(--fs-pill)', color: 'var(--muted)' }}>
                    {String(c.value)}
                  </span>
                ) : null}
              </div>
              {c.detail ? (
                <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--muted)', marginTop: 2 }}>{c.detail}</div>
              ) : null}
              {Array.isArray(c.bullets) && c.bullets.length ? (
                <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 'var(--fs-pill)' }}>
                  {c.bullets.map((b, i) => <li key={i}>{typeof b === 'string' ? b : b?.text || ''}</li>)}
                </ul>
              ) : null}
              {c.source ? (
                <div className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)', marginTop: 3 }}>
                  {c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.source}</a> : c.source}
                </div>
              ) : null}
            </div>
          )
        })}

        {!criteria.length ? (
          <div className="card-sub">Not profiled for this model yet.</div>
        ) : null}
      </div>
    </div>
  )
}

function FitAnalysis({ data, onChanged }) {
  if (!data.wc_fit && !data.proc_fit) return <Empty>No fit model has scored this account yet.</Empty>
  return (
    <>
      <FitModel title="Working capital fit" fit={data.wc_fit} slug={data.slug} model="wc" onChanged={onChanged} />
      <FitModel title="Procurement fit" fit={data.proc_fit} slug={data.slug} model="proc" onChanged={onChanged} />
    </>
  )
}

export default function AccountDetail({ slug, onClose, onChanged }) {
  // Four tabs instead of one long scroll plus a hidden rail. Declared with
  // the other hooks: below the early returns it changed the hook count
  // between renders and React refused to render the panel at all.
  const [tab, setTab] = useState('focus')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [draft, setDraft] = useState(null)
  const [drafting, setDrafting] = useState(false)

  const [editing, setEditing] = useState(null)
  const [type, setType] = useState('email')
  const [note, setNote] = useState('')
  const [contact, setContact] = useState('')
  const [stepText, setStepText] = useState('')
  const [stepDue, setStepDue] = useState('')

  const reload = () => api.account(slug).then(setData).catch(setError)
  useEffect(() => {
    setData(null)
    setDraft(null)
    reload()
  }, [slug])

  if (error) return <div className="slide-body"><div className="col"><ErrorNote error={error} /></div></div>
  if (!data) return <div className="slide-body"><div className="col"><Empty>Loading…</Empty></div></div>

  const e = data.engagement
  const w = data.inv_days_working

  const logTouch = async () => {
    if (!note.trim()) return
    await api.addTouch({ account_slug: slug, type, contact: contact || null, note: note.trim() })
    setNote('')
    await reload()
    onChanged?.()
  }

  const saveStep = async () => {
    await api.setNextStep(slug, { text: stepText.trim(), due: stepDue || null })
    setStepText('')
    setStepDue('')
    await reload()
    onChanged?.()
  }

  const makeDraft = async () => {
    setDrafting(true)
    setDraft(null)
    try {
      setDraft(await api.draft({ slug }))
    } catch (err) {
      setDraft({ error: err.message })
    } finally {
      setDrafting(false)
    }
  }

  const setPriority = async (v) => {
    await api.setPriority(slug, v)
    await reload()
    onChanged?.()
  }

  const changed = async () => {
    await reload()
    onChanged?.()
  }

  return (
    <>
      <div className="slide-head">
        <button className="slide-close" onClick={onClose} title="Close (Esc)" aria-label="Close">&times;</button>
        <h1>{data.name}</h1>
        {data.ticker ? <span className="topbar-note">{data.ticker}</span> : null}
        {/* Nullable on purpose: a company that publishes nothing, or one nobody
            has read yet, gets no rating rather than a Low that reads as a
            verdict. */}
        <Chip kind={data.fit === 'High' ? 'named' : data.fit === 'Medium' ? 'mentioned' : data.fit ? 'plain' : 'unresearched'}>
          {data.fit
            ? `${data.fit.toUpperCase()} FIT`
            : data.disclosure === 'none'
            ? 'NO PUBLIC DISCLOSURE'
            : 'NOT RESEARCHED'}
        </Chip>
        {/* The structural read, beside the evidence-based one. They answer
            different questions and can legitimately disagree. */}
        {data.wc_fit ? (
          <Chip
            kind={
              data.wc_fit.fit === 'Strong' ? 'named' : data.wc_fit.fit === 'Possible' ? 'mentioned' : data.wc_fit.fit ? 'not_now' : 'unresearched'
            }
          >
            {data.wc_fit.fit
              ? `WC ${data.wc_fit.fit.toUpperCase()}${data.wc_fit.provisional ? '*' : ''} ${data.wc_fit.met}/4`
              : `WC UNRATED ${data.wc_fit.met}/4`}
          </Chip>
        ) : null}
        {data.ownership ? <Chip kind="plain">{data.ownership.toUpperCase()}</Chip> : null}
        <span className="row" style={{ gap: 5 }}>
          <span className="lbl">Priority</span>
          <PriorityPicker value={data.priority} onChange={setPriority} />
        </span>
        <div className="topbar-right">
          <button className="btn ghost" onClick={makeDraft} disabled={drafting}>
            {drafting ? 'Drafting…' : 'Draft outreach'}
          </button>
          {e.status !== 'not_started' ? <Chip kind={e.status}>{fmt.status(e.status)}</Chip> : null}
        </div>
      </div>

      <div className="slide-body">
        <div className="col">
          <div className="kpi-strip" style={{ gap: 0, border: '0.5px solid var(--line)', background: 'var(--line)' }}>
            {[
              ['Revenue', <Money value={data.revenue} fallback={data.revenue_fallback} />, data.revenue_note],
              ['Cost of goods', <Money value={data.cogs} />, 'USD millions'],
              ['Margin', data.margin || '—', data.margin_basis],
              ['Inventory days', fmt.days(data.inv_days), data.inv_days_note || data.dio_note || (data.inv_days_basis === 'stated' ? 'stated' : 'calculated')],
              // The two structural models, alongside the reported figures. The
              // old "areas named" tile went with the Named/Mentioned columns.
              ['WC fit', data.wc_fit?.fit || '—', data.wc_fit ? `${data.wc_fit.met} of 4 criteria` : 'not profiled'],
              ['Procurement fit', data.proc_fit?.fit || (data.proc_fit?.below_gate ? 'Below gate' : '—'),
                data.proc_fit ? `${data.proc_fit.met} of 4 criteria` : 'not profiled'],
            ].map(([label, value, sub]) => (
              <div key={label} style={{ flex: 1, background: 'var(--panel)', padding: '9px 13px', minWidth: 0 }}>
                <div className="kpi-label">{label}</div>
                <div className="mono" style={{ fontSize: 'var(--fs-prominent)', fontWeight: 600 }}>{value}</div>
                {sub ? <div className="kpi-sub" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div> : null}
              </div>
            ))}
          </div>

          <div className="tabbar">
            {[
              ['focus', 'Focus areas', null],
              ['fit', 'Fit analysis', fitSummary(data)],
              ['contacts', 'Contacts', data.contacts?.length || 0],
              ['engagement', 'Engagement', e.touch_count || null],
            ].map(([key, label, badge]) => (
              <button key={key} className={`tab ${tab === key ? 'on' : ''}`} onClick={() => setTab(key)}>
                {label}
                {badge != null && badge !== '' ? (
                  <span className="mono" style={{ marginLeft: 5, color: 'var(--tertiary)', fontSize: '10px' }}>{badge}</span>
                ) : null}
              </button>
            ))}
          </div>

          {tab === 'focus' ? (
          <>
          {w ? (
            <div className="card">
              <div className="card-header">
                <span className="card-title">How inventory days are calculated</span>
                <span className="card-sub">nobody in this sector publishes it</span>
              </div>
              <div className="card-body">
                <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
                  {[
                    ['Inventory', w.inventory, `${w.currency || ''} millions`],
                    ['Cost of goods', w.cogs, `${w.currency || ''} millions`],
                    ['Days in period', w.n, w.basis],
                  ].map(([label, value, unit]) => (
                    <div key={label}>
                      <div className="lbl">{label}</div>
                      <div className="mono" style={{ fontSize: 'var(--fs-prominent)', fontWeight: 600 }}>
                        {value.toLocaleString('en-GB')}
                      </div>
                      <div className="kpi-sub">{unit}</div>
                    </div>
                  ))}
                  <div style={{ alignSelf: 'center', color: 'var(--tertiary)' }}>=</div>
                  <div>
                    <div className="lbl">Inventory days</div>
                    <div className="mono" style={{ fontSize: 'var(--fs-prominent)', fontWeight: 600, color: 'var(--accent-ink)' }}>
                      {w.days.toFixed(1)}
                    </div>
                    <div className="kpi-sub">days</div>
                  </div>
                </div>
                <div className="card-sub" style={{ marginTop: 9 }}>
                  Both figures are {w.currency || 'reporting-currency'} millions from the same period, so the ratio is
                  currency neutral. {w.label ? `${w.label}. ` : ''}Calculated from the filings, not disclosed by the company.
                </div>
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-header">
              <span className="card-title">Focus areas</span>
              <span className="card-sub">hover [src] for the source</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5px', background: 'var(--line-soft)' }}>
              {data.focus.map((f) => (
                <div key={f.key} style={{ background: 'var(--panel)', padding: '11px 14px' }}>
                  <div className="row" style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{f.label}</span>
                    <Chip kind={f.status}>{f.status.toUpperCase()}</Chip>
                    {f.figure ? <span className="card-sub" style={{ marginLeft: 'auto' }}>{f.figure}</span> : null}
                  </div>
                  {f.status === 'unresearched' ? (
                    <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ai-soft-ink)' }}>
                      Never scored. This account is in the pending top-up batch.
                    </div>
                  ) : f.evidence ? (
                    <Bullets text={f.evidence} note={f.source_note} />
                  ) : (
                    <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--tertiary)' }}>No detail recorded.</div>
                  )}
                  {f.gap ? (
                    <div className="card-sub" style={{ marginTop: 4, color: 'var(--warn)' }}>Gap: {f.gap}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Why this is an opportunity</span>
              <span className="card-sub">{data.sources.length} sources · researched {fmt.date(data.first_researched)}</span>
            </div>
            <div className="card-body stack">
              {data.opportunity?.why ? (
                <div style={{ borderLeft: '2px solid var(--accent)', paddingLeft: 12 }}>
                  <Bullets text={data.opportunity.why} />
                </div>
              ) : null}

              {data.quote?.[0] ? (
                <div style={{ background: 'var(--bg)', border: '0.5px solid var(--line)', padding: '12px 14px' }}>
                  <div style={{ fontSize: 'var(--fs-title)', fontStyle: 'italic', lineHeight: 1.5 }}>“{data.quote[0]}”</div>
                  <div className="card-sub" style={{ marginTop: 6 }}>
                    {[data.quote[1], data.quote[2], data.quote[3]].filter(Boolean).join(' · ')}
                  </div>
                </div>
              ) : null}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
                {[['Way in', data.opportunity?.entry], ['Buyer', data.opportunity?.buyer], ['Watch out', data.opportunity?.watch]].map(
                  ([label, text]) =>
                    text ? (
                      <div key={label}>
                        <div className="lbl" style={{ marginBottom: 4 }}>{label}</div>
                        <Bullets text={text} />
                      </div>
                    ) : null,
                )}
              </div>

              {/* The AI research was captured for the deep-tier accounts and then
                  never displayed. It is the evidence for whether a company is
                  already doing this work, and where, so it belongs on the page. */}
              {data.ai?.length ? (
                <div style={{ marginTop: 12 }}>
                  <div className="lbl" style={{ color: 'var(--ai-ink)', marginBottom: 6 }}>
                    AI and digital signals{data.ai_posture ? ` · ${data.ai_posture}` : ''}
                  </div>
                  <div className="stack" style={{ gap: 8 }}>
                    {data.ai.map((row, i) => {
                      const [label, finding, source] = Array.isArray(row) ? row : [null, row, null]
                      return (
                        <div key={i} style={{ background: 'var(--ai-tint)', border: '0.5px solid var(--ai-border)', padding: '8px 11px' }}>
                          {label ? (
                            <div className="lbl" style={{ color: 'var(--ai-ink)', marginBottom: 2 }}>{label}</div>
                          ) : null}
                          <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink)' }}>{finding}</div>
                          {source ? (
                            <div className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)', marginTop: 3 }}>{source}</div>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {/* Links to other companies in the book. A distributor or a former
                  parent is a warmer route in than a cold approach. */}
              {data.relationships?.length ? (
                <div style={{ marginTop: 12 }}>
                  <div className="lbl" style={{ marginBottom: 6 }}>Connections</div>
                  <div className="stack" style={{ gap: 6 }}>
                    {data.relationships.map((rel, i) => (
                      <div key={i} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                        <Chip kind={rel.covered ? 'acct' : 'plain'}>{(rel.type || 'linked').replace(/_/g, ' ').toUpperCase()}</Chip>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 500 }}>{rel.counterparty}</span>
                          {rel.covered ? (
                            <span className="mono" style={{ fontSize: '10px', color: 'var(--pos)', marginLeft: 5 }}>IN BOOK</span>
                          ) : null}
                          {rel.note ? (
                            <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--muted)' }}>{rel.note}</div>
                          ) : null}
                          {rel.source ? (
                            <div className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)' }}>{rel.source}</div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="row" style={{ alignItems: 'stretch', gap: 10 }}>
                {data.vendor ? (
                  <div className="notice grow" style={{ display: 'block' }}>
                    <div className="lbl" style={{ color: 'var(--ai-ink)', marginBottom: 3 }}>Incumbent vendor</div>
                    <div className="notice-body">{data.vendor}</div>
                  </div>
                ) : null}
                {data.gaps ? (
                  <div className="grow" style={{ background: 'var(--neg-soft)', border: '0.5px solid #F0D2D2', padding: '9px 12px' }}>
                    <div className="lbl" style={{ color: 'var(--neg)', marginBottom: 3 }}>Evidence gap</div>
                    <div style={{ fontSize: 'var(--fs-pill)', color: '#8B2020' }}>{data.gaps}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {draft ? (
            <div className="card" style={{ background: 'var(--ai-tint)', borderColor: 'var(--ai-border)' }}>
              <div className="card-header" style={{ borderColor: 'var(--ai-border)' }}>
                <span className="card-title" style={{ color: 'var(--ai-ink)' }}>Drafted opening</span>
                <span className="card-sub">
                  {draft.facts_cited?.length ? `${draft.facts_cited.length} facts cited` : ''}
                  {draft.cost ? ` · cost ${draft.cost.usd_display}` : ''}
                </span>
              </div>
              <div className="card-body">
                {draft.error ? (
                  <ErrorNote error={{ message: draft.error }} />
                ) : (
                  <>
                    {draft.to ? <div className="card-sub" style={{ marginBottom: 6 }}>TO {draft.to.name?.toUpperCase()}, {draft.to.title}</div> : null}
                    {draft.subject ? <div style={{ fontWeight: 500, marginBottom: 7 }}>{draft.subject}</div> : null}
                    <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{draft.body}</div>
                    <div className="row" style={{ marginTop: 11 }}>
                      <button className="btn primary" onClick={() => navigator.clipboard?.writeText(`${draft.subject || ''}\n\n${draft.body}`)}>
                        Copy to mail
                      </button>
                      <button className="btn" onClick={makeDraft}>Redraft</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-header"><span className="card-title">Sources</span></div>
            <div className="card-body">
              {data.sources.map((s, i) => (
                <div key={i} className="row" style={{ alignItems: 'baseline', padding: '2px 0' }}>
                  <span className="mono" style={{ fontSize: 'var(--fs-badge)', color: 'var(--accent-ink)', width: 18 }}>{i + 1}</span>
                  <span style={{ fontSize: 'var(--fs-pill)', color: 'var(--muted)' }}>{s}</span>
                </div>
              ))}
            </div>
          </div>
          </>
          ) : null}

          {tab === 'fit' ? <FitAnalysis data={data} onChanged={changed} /> : null}

          {tab === 'contacts' ? (
            <div className="card">
            <div style={{ padding: '10px 16px' }}>
              <div className="row" style={{ marginBottom: 7 }}>
                <span className="lbl">Contacts</span>
                <span className="card-sub" style={{ marginLeft: 'auto' }}>
                  {data.contact_count} on file{data.contacts.length > data.contact_count ? ` · ${data.contacts.length - data.contact_count} absent role${data.contacts.length - data.contact_count === 1 ? '' : 's'} recorded` : ''}
                </span>
              </div>
              {data.contacts.map((c) => (
                <div key={c.name + (c.title || '')} style={{ padding: '4px 0', borderBottom: '0.5px solid var(--line-soft)' }}>
                  <div className="row" style={{ alignItems: 'baseline' }}>
                    <span style={{ fontSize: 'var(--fs-pill)', fontWeight: 500 }}>{c.name}</span>
                    {c.is_placeholder ? <Chip kind="absent">ROLE ABSENT</Chip> : null}
                    {c.origin && c.origin !== 'research' ? (
                      <Chip kind={c.origin === 'added' ? 'named' : 'replied'}>{c.origin.toUpperCase()}</Chip>
                    ) : null}
                    <button className="btn link" style={{ marginLeft: 'auto' }} onClick={() => setEditing({ ...c })}>edit</button>
                    {c.linkedin ? (
                      <a className="mono" style={{ fontSize: 'var(--fs-badge)' }} href={c.linkedin} target="_blank" rel="noreferrer">in</a>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--muted)' }}>{c.title}</div>
                  {c.email || c.phone ? (
                    <div className="mono" style={{ fontSize: 'var(--fs-badge)', color: 'var(--ink-soft)' }}>
                      <Email contact={c} />
                      {c.phone ? ` · ${c.phone}` : ''}
                    </div>
                  ) : null}
                  {c.note ? <div style={{ fontSize: 'var(--fs-badge)', color: 'var(--muted)' }}>{c.note}</div> : null}
                </div>
              ))}

              <button className="btn link" style={{ marginTop: 8 }} onClick={() => setEditing({})}>+ Add a contact</button>
            </div>
            </div>
          ) : null}

          {tab === 'engagement' ? (
            <div className="card">
            <div style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--line)' }}>
              <div className="row" style={{ marginBottom: 8 }}>
                <span className="card-title">Engagement</span>
                <Chip kind={e.status}>{fmt.status(e.status)}</Chip>
              </div>
              <div style={{ background: 'var(--bg)', border: '0.5px solid var(--line)', padding: '9px 11px' }}>
                <div className="lbl" style={{ marginBottom: 3 }}>Next step</div>
                {e.next_step ? (
                  <>
                    <div style={{ fontWeight: 500 }}>{e.next_step.text}</div>
                    <div className="kpi-sub">{e.next_step.due ? `due ${fmt.date(e.next_step.due)}` : 'no date set'}</div>
                  </>
                ) : (
                  <div style={{ color: 'var(--neg)', fontSize: 'var(--fs-pill)' }}>Nothing scheduled.</div>
                )}
                <div className="row" style={{ marginTop: 7 }}>
                  <input className="input" placeholder="What happens next" value={stepText} onChange={(ev) => setStepText(ev.target.value)} />
                  <input className="input" type="date" style={{ width: 130 }} value={stepDue} onChange={(ev) => setStepDue(ev.target.value)} />
                  <button className="btn primary" onClick={saveStep} disabled={!stepText.trim()}>Set</button>
                </div>
              </div>
            </div>

            <div style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--line)' }}>
              <div className="lbl" style={{ marginBottom: 8 }}>Log a touch</div>
              <div className="row" style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                {TOUCH_TYPES.map((t) => (
                  <button key={t} className={`btn ${type === t ? 'on' : ''}`} onClick={() => setType(t)}>
                    {t[0].toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
              <textarea className="input" placeholder="What happened, and what it changes" value={note} onChange={(ev) => setNote(ev.target.value)} />
              <div className="row" style={{ marginTop: 8 }}>
                <select className="input" style={{ width: 170 }} value={contact} onChange={(ev) => setContact(ev.target.value)}>
                  <option value="">No contact</option>
                  {(data.contacts || []).map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={logTouch} disabled={!note.trim()}>Save</button>
              </div>
            </div>

            <div style={{ padding: '12px 16px' }}>
              <div className="lbl" style={{ marginBottom: 10 }}>History</div>
              {e.touches.length === 0 ? (
                <Empty>Nothing logged yet.</Empty>
              ) : (
                e.touches.map((t, i) => (
                  <div className="tl" key={t.id}>
                    <div className="tl-rail">
                      <div className="tl-dot" style={{ background: t.type === 'reply' ? 'var(--blue)' : 'var(--accent)' }} />
                      {i < e.touches.length - 1 ? <div className="tl-line" /> : null}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="tl-when">
                        {fmt.date(t.at).toUpperCase()} · {t.type.toUpperCase()}
                        {t.contact ? ` · ${t.contact}` : ''}
                      </div>
                      <div className="tl-what">{t.note}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>
          ) : null}
        </div>
      </div>

      {editing ? (
        <ContactDialog
          slug={slug}
          accountName={data.name}
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      ) : null}
    </>
  )
}
